import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, Loading } from "@/components/Common";
import { ReturnForm } from "@/components/returns/ReturnForm";
import { RetPill, DaysCell, RetLink, orderOf, useRetMeta, Txt, Fld, Card } from "@/components/returns/RetCommon";
import { useAuth } from "@/context/AuthContext";
import { useLoad } from "@/hooks/useData";
import { usePref } from "@/hooks/usePref";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { dateLabel, todayStr, fmtINR } from "@/lib/format";

const BulkRefund = ({ ids, onDone }) => {
  const [d, setD] = useState(todayStr());
  const run = async () => {
    try { const { data } = await api.post("/returns/bulk-refund", { ids, refund_date: d }); toast.success(`${data.updated} refunds marked processed`); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return <div className="flex items-end gap-2" data-testid="bulk-refund"><Fld label="Refund date"><Txt type="date" value={d} onChange={setD} testid="bulk-refund-date" /></Fld>
    <Button size="sm" disabled={!ids.length} onClick={run} data-testid="bulk-refund-btn">Mark {ids.length} refunded</Button></div>;
};

export default function ActionQueue() {
  const meta = useRetMeta();
  const { can, isAdmin } = useAuth();
  const [tab, setTab] = usePref("returns.queue.tab", "Find order");
  const [open, setOpen] = useState(null);
  const [sel, setSel] = useState([]);
  const counts = useLoad(() => api.get("/returns", { params: { open: true } }).then((r) => r.data.counts), []);
  const { data, reload } = useLoad(() => api.get("/returns", { params: { next_action: tab, oldest: true } }).then((r) => r.data), [tab]);
  const detail = useLoad(() => (open ? api.get(`/returns/${open}`).then((r) => r.data) : Promise.resolve(null)), [open]);
  if (!meta) return <Loading />;
  const done = () => { setOpen(null); setSel([]); reload(); counts.reload(); };
  const money = isAdmin || can("returns_money", "edit");
  return (
    <div data-testid="action-queue">
      <PageHeader title="Action Queue" subtitle="One tab per next step, oldest first. Click a row to act on it." />
      <div className="mb-3 flex flex-wrap gap-2">{meta.actions.map((a) => (
        <button key={a.action} onClick={() => { setTab(a.action); setOpen(null); setSel([]); }} data-testid={`queue-tab-${a.action.replace(/\W+/g, "-").toLowerCase()}`}
          className={cn("rounded-full border px-3 py-1.5 text-xs font-semibold", tab === a.action ? "border-primary bg-primary text-white" : "bg-white hover:border-primary/50")}>
          {a.action} <span className="ml-1 font-mono">{counts.data?.[a.action] || 0}</span><span className="ml-1 opacity-70">· {a.owner}</span></button>))}</div>
      {tab === "Process refund" && money && <Card title="Bulk refund"><BulkRefund ids={sel} onDone={done} /></Card>}
      {!data ? <Loading /> : (
        <div className="panel mt-3 overflow-x-auto">
          <table className="data-table" data-testid="queue-table">
            <thead><tr>{tab === "Process refund" && <th />}<th>Return</th><th>Date</th><th>Order / Invoice</th><th>Tracking</th><th>Product</th><th className="text-right">Value</th><th>Status</th><th className="text-right">Days</th></tr></thead>
            <tbody>{data.rows.map((r) => (
              <tr key={r.id} className={cn("cursor-pointer hover:bg-amber-50/40", open === r.id && "bg-amber-50")} onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`queue-row-${r.return_id}`}>
                {tab === "Process refund" && <td onClick={(e) => e.stopPropagation()}><Checkbox checked={sel.includes(r.id)} onCheckedChange={(c) => setSel(c ? [...sel, r.id] : sel.filter((x) => x !== r.id))} data-testid={`queue-sel-${r.return_id}`} /></td>}
                <td><RetLink r={r} /></td><td className="text-xs">{dateLabel(r.return_date)}</td><td className="font-mono text-xs">{orderOf(r)}</td><td className="font-mono text-xs">{r.tracking_id}</td>
                <td><div className="max-w-[220px] truncate text-xs">{r.product_name}</div></td><td className="num">{r.order_value ? fmtINR(r.order_value) : "—"}</td>
                <td><RetPill status={r.status} /></td><td className="num"><DaysCell days={r.days_open} amber={meta.settings.amber_days} red={meta.settings.red_days} /></td>
              </tr>))}</tbody>
          </table>
          {!data.rows.length && <div className="py-10 text-center text-sm text-muted-foreground">Nothing waiting here.</div>}
          {data.total > data.rows.length && <div className="p-2 text-xs text-muted-foreground">Showing the oldest {data.rows.length} of {data.total}.</div>}
        </div>)}
      {open && detail.data && <div className="mt-4" data-testid="queue-panel"><ReturnForm meta={meta} initial={detail.data} onSaved={done} /></div>}
    </div>
  );
}
